import { TextField } from '@mui/material';
import { Tabs } from '@mui/material';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import React, { SetStateAction, useEffect, useState } from 'react';
import { Button, Card, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { BiCopy } from 'react-icons/bi';
import { useSelector } from 'react-redux';
import { AccessRightEnum } from '../../../../../../../enum/AccessRightEnum';
import { CustomImageHandler } from '../../../../../../../shared/components/CustomImageHandler';
import { MessageBody } from '../../../../../../../shared/components/message/MessageBody';
import ToggleSwitch from '../../../../../../../shared/components/ToggleSwitch';
import {
    downloadFile,
    formatDate,
    getCorrectImageThumbnail,
    getExternalLink,
    hasAccessRights,
    sanitizeUrl,
} from '../../../../../../../shared/functions/Functions';
import { IAllStates } from '../../../../../../../store/rootReducer';
import { ErrorToast, SuccessToast } from '../../../../../../../utils/toasters';
import { ManageService } from '../../../service';
import { iManageAttachments, iManageList } from '../../dto/IManageCalendar';
import { statusesToHideApproveButton } from './CalendarUpdateModal';
import { a11yProps, TabPanel } from '../../../../../../../shared/components/MaterialTabsComponents';
import FilePlaceholder from '../../../../../../../shared/components/placeholder/FilePlaceholder';
import { Fancybox } from '../../../../../../../shared/components/Fancybox';
import { InfluencerReportingService } from '../../../../../../influencer/reporting/service';
import { InfluencerPlannerService } from '../../../../../../influencer/planner/service';
import {
    IExternalLink,
    initialExternalState,
} from '../../../../../../influencer/planner/components/preview/PreviewContentFiels';
import axios, { CancelTokenSource } from 'axios';
import UploadingModal from '../../../../../../influencer/planner/components/UploadingModal';
import { UploadImage } from '../../../../../../../shared/components/UploadImage';
import { BsFillTrashFill } from 'react-icons/bs';
import { ConfirmationModal } from '../../../../../../../shared/components/modal/ConfirmationModal';

interface IProps {
    state: iManageList;
    setState: React.Dispatch<SetStateAction<iManageList>>;
    setCurrTab: React.Dispatch<SetStateAction<number>>;
    currTab: number;
    handleCopy: (val: string) => void;
    lastMessageRef: React.RefObject<HTMLInputElement>;
    changeCaptionHandler: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const CalendarManageRightCard = ({
    state,
    handleCopy,
    setState,
    lastMessageRef,
    setCurrTab,
    currTab,
    changeCaptionHandler,
}: IProps) => {
    const [locks, setLocks] = useState<{ isStorylineLocked: boolean; isCaptionLocked: boolean }>({
        isStorylineLocked: false,
        isCaptionLocked: false,
    });

    const [showInputField, setShowInputField] = useState(false);
    const [externalLink, setExternalLink] = useState<IExternalLink>(initialExternalState);
    const [errorExternalLink, setErrorExternalLink] = useState<boolean>(false);
    const [isFileUploading, setIsFileUploading] = useState(false);
    const [filesToUpload, setFilesToUpload] = useState<Blob[] | MediaSource[]>();
    const [selectedFile, setSelectedFile] = useState<iManageAttachments>();
    const [uploadData, setUploadData] = useState({
        loaded: 0,
        from: 0,
    });
    const [source, setSource] = useState<undefined | CancelTokenSource>(axios.CancelToken.source());

    const { user } = useSelector((states: IAllStates) => states.auth);
    const { t } = useTranslation();
    // const showInfluencerFeedback = !hasAccessRights(AccessRightEnum.INFLUENCER_FEEDBACK_HIDE);
    // const showClientComments = hasAccessRights(AccessRightEnum.MANAGE_CAN_VIEW_CLIENT_COMMENTS);

    const showInfluencerFeedback =
        state?.showInfluencerComments ?? !hasAccessRights(AccessRightEnum.INFLUENCER_FEEDBACK_HIDE);
    const showClientComments =
        state?.showClientComments ?? hasAccessRights(AccessRightEnum.MANAGE_CAN_VIEW_CLIENT_COMMENTS);

    const isCaptionVisible = !['instagram_story'].includes(state?.offerItem?.socialPlatform?.name);
    const isDisabled = ['reported', 'approved'].includes(state?.status);

    useEffect(() => {
        if (!!filesToUpload?.length) {
            handleUploadFiles();
        }
    }, [filesToUpload?.length]);

    const handleChangeTab = (event: React.SyntheticEvent<Element, Event>, value: number) => {
        setCurrTab(value);
        setTimeout(() => {
            lastMessageRef?.current?.scrollIntoView({
                behavior: 'smooth',
            });
        }, 0);
    };
    const handleSwitch = (attachmentId: number, isApproved: boolean) => {
        const copy = state;
        const indexToReplace = copy?.attachments?.findIndex(attachment => attachment?.id === attachmentId);
        copy.attachments[indexToReplace] = {
            ...copy.attachments[indexToReplace],
            isApproved: !isApproved,
        };
        ManageService.updateFileApprovement(
            state?.id,
            copy?.attachments?.map(attachment => ({
                id: attachment?.id,
                isApproved: attachment?.isApproved,
            }))
        )
            .then(response => {
                setState(response);
                SuccessToast(t('general.toasts.successUpdated'));
            })
            .catch(error => ErrorToast(error));
    };

    const handleLocks = (checked: boolean, lock: 'isStorylineLocked' | 'isCaptionLocked') => {
        const service =
            lock === 'isStorylineLocked'
                ? ManageService.putLockStoryline(state?.id)
                : ManageService.putLockCaption(state?.id);
        service
            .then(() => {
                setState(prev => ({ ...prev, [lock]: checked }));
                SuccessToast(t('general.toasts.successUpdated'));
            })
            .catch(error => ErrorToast(error));
    };

    useEffect(() => {
        setLocks({
            isStorylineLocked: state?.isStorylineLocked,
            isCaptionLocked: state?.isCaptionLocked,
        });
    }, [state]);
    const submitAttachmentHandler = (isUpdate = false) => {
        setErrorExternalLink(!sanitizeUrl(externalLink.link));
        if (!sanitizeUrl(externalLink.link)) {
            if (isUpdate) {
                ErrorToast({ response: { data: { message: t('general.toasts.error_external_link') } } });
            }
            return;
        }

        if (isUpdate) {
            InfluencerReportingService.updateExternalLink(externalLink.id, externalLink.link)
                .then(response => {
                    if (response?.id) {
                        setExternalLink(initialExternalState);
                        setShowInputField(false);
                        SuccessToast(t('general.toasts.successUpdated'));
                        setState(prev => ({ ...prev, attachments: response?.attachments }));
                    }
                })
                .catch(error => ErrorToast(error));
        } else {
            InfluencerReportingService.createExternalLink(externalLink?.id, externalLink?.link)
                .then(response => {
                    if (response?.id) {
                        setState(prev => ({ ...prev, attachments: response?.attachments }));
                        setShowInputField(false);
                        SuccessToast(t('general.toasts.successUpdated'));
                        setExternalLink(initialExternalState);
                    }
                })
                .catch(error => ErrorToast(error));
        }
    };

    const handleUploadFiles = async () => {
        const formData = new FormData();

        if (!!filesToUpload?.length) {
            for (const item of filesToUpload) {
                formData.append('files', item as string | Blob);
            }
        }
        setIsFileUploading(true);
        await InfluencerPlannerService.uploadAttachment(formData, state?.id, source?.token as any, (e: ProgressEvent) =>
            progressHandler(e)
        )
            .then(response => {
                setState(prev => ({ ...prev, attachments: response?.attachments }));
                setFilesToUpload([]);
                setSelectedFile(undefined);
                setUploadData({
                    loaded: 0,
                    from: 0,
                });
                SuccessToast(t('influencer.planner.successUploadedFile'));
            })
            .catch(error => ErrorToast(error))
            .finally(() => setIsFileUploading(false));
    };

    const deleteImageHandler = async () => {
        await InfluencerPlannerService.deleteAttachment(selectedFile?.id as number)
            .then(response => {
                setState(prevState => ({ ...prevState, attachments: response?.attachments }));
                SuccessToast(
                    `${t('influencer.planner.successDeletedFile')} ${
                        selectedFile?.originalName ?? selectedFile?.name ?? selectedFile?.externalLink
                    }`
                );
            })
            .catch(error => ErrorToast(error));
    };

    const progressHandler = (e: ProgressEvent) => {
        setUploadData({
            loaded: e.loaded,
            from: e.total,
        });
    };

    const cancelApiCall = () => {
        source?.cancel('API request cancelled');
        setSource(undefined);
    };

    const handleCancelAttachmentHandler = () => {
        setShowInputField(false);
        setExternalLink(initialExternalState);
    };
    const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value, id } = event.target;
        setExternalLink(prev => ({ ...prev, link: value, id: +id }));
    };
    return (
        <>
            <UploadingModal
                show={isFileUploading}
                uploadData={uploadData}
                closeModal={() => setIsFileUploading(false)}
                cancelApiCall={cancelApiCall}
            />
            <ConfirmationModal
                title={t('influencer.planner.deleteImage')}
                description={`${t('influencer.planner.deleteImageDesc')} ${
                    selectedFile?.externalLink ?? selectedFile?.originalName ?? '-'
                }`}
                action={deleteImageHandler}
                show={!!selectedFile?.id}
                closeModal={() => setSelectedFile(undefined)}
            />
            <div className="my-3 ">
                <div className="d-flex align-items-center mb-2">
                    <h6 className="w-100 mb-0">Storyline</h6>

                    <div>
                        <ToggleSwitch
                            onChange={(e: any) => handleLocks(e, 'isStorylineLocked')}
                            small={true}
                            id={`Lock Storyline`}
                            checked={locks?.isStorylineLocked}
                            disabled={statusesToHideApproveButton?.includes(state?.status)}
                        />
                    </div>
                </div>
                <div>
                    <TextField
                        label="Storyline"
                        name="storyline"
                        variant="outlined"
                        multiline
                        InputProps={{
                            endAdornment: (
                                <BiCopy className="copy-icon pr-1" onClick={() => handleCopy(state?.storyline ?? '')} />
                            ),
                        }}
                        className="w-100"
                        size="small"
                        disabled={locks?.isStorylineLocked || !showInfluencerFeedback}
                        value={state?.storyline ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            (['scheduled', 'feedback_loop'].includes(state?.status) || !locks?.isStorylineLocked) &&
                            changeCaptionHandler(e)
                        }
                    />
                </div>
            </div>
            {isCaptionVisible && (
                <>
                    <div className="d-flex align-items-center">
                        <div className="d-md-flex align-items-center mt-3 mb-2 w-100">
                            <h6 className="mb-0">{t('influencer.planner.caption')} -</h6>
                            <div className="text-muted font-weight-normal ml-md-1 mt-1">
                                {t('influencer.planner.copyDescription')}
                            </div>
                        </div>

                        <ToggleSwitch
                            onChange={(e: any) => handleLocks(e, 'isCaptionLocked')}
                            small={true}
                            id={`Lock Caption`}
                            checked={locks?.isCaptionLocked}
                            disabled={statusesToHideApproveButton?.includes(state?.status)}
                        />
                    </div>
                    <div>
                        <TextField
                            label="Caption"
                            name="caption"
                            variant="outlined"
                            multiline
                            InputProps={{
                                endAdornment: (
                                    <BiCopy
                                        className="copy-icon pr-1"
                                        onClick={() => handleCopy(state?.caption ?? '')}
                                    />
                                ),
                            }}
                            className="w-100"
                            size="small"
                            disabled={locks?.isCaptionLocked || !showInfluencerFeedback}
                            value={state?.caption ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                (['scheduled', 'feedback_loop'].includes(state?.status) || !locks?.isCaptionLocked) &&
                                changeCaptionHandler(e)
                            }
                        />
                    </div>
                </>
            )}

            <h6 className=" mt-3">{t('influencer.planner.contentFiles')}</h6>
            {showInfluencerFeedback && !isDisabled && (
                <Row>
                    <div className="col-md-5 mb-md-3 mb-2">
                        <UploadImage
                            saveFile={
                                setFilesToUpload as React.Dispatch<SetStateAction<Blob[] | MediaSource[] | undefined>>
                            }
                            classes="px-0"
                            numberOfFiles={filesToUpload?.length}
                            disabled={isDisabled}
                            isSmall={true}
                            acceptedFormats="video/*, image/jpeg, image/png, image/jpg, application/pdf, audio/mpeg, audio/x-m4a"
                            maxSize={Number(process.env.VITE_PLANNER_MAX_FILE_SIZE)}
                            isMultiple={true}
                            maxFiles={20}
                        />
                    </div>
                    <div className={`col-md-1 d-flex justify-content-center ${showInputField ? 'invisible' : ''}`}>
                        <span className="text-muted mb-md-4 mt-md-5">OR</span>
                    </div>
                    <div className={`col-md-6 pl-md-0 my-4 ${showInputField ? 'invisible' : ''}`}>
                        <div className="d-flex align-items-center">
                            <div className="col px-0">
                                <TextField
                                    label="External Link"
                                    variant="outlined"
                                    size="small"
                                    name="link"
                                    id={String(state?.id)}
                                    disabled={isDisabled}
                                    value={externalLink?.link ?? ''}
                                    onChange={changeHandler}
                                    className="w-100"
                                />
                                {errorExternalLink && (
                                    <p className="text-danger mt-1 mb-0">{t('general.toasts.error_external_link')}</p>
                                )}
                            </div>
                            <Button
                                variant="primary"
                                className="ml-2"
                                disabled={isDisabled || !externalLink?.link}
                                onClick={() => submitAttachmentHandler(false)}
                            >
                                {t('general.button.create')}
                            </Button>
                        </div>
                    </div>
                </Row>
            )}

            <Fancybox delegate='[data-fancybox="gallery-attachments"]'>
                <Row>
                    {!!state?.attachments?.length ? (
                        state?.attachments?.map((attachment, index) => {
                            return (
                                <Col md="6" className="my-2" key={attachment?.id}>
                                    <Card className="h-100">
                                        <Card.Body className="d-flex align-items-center flex-row">
                                            {!!attachment?.externalLink ? (
                                                <a
                                                    href={getExternalLink(attachment?.externalLink)}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="cursor-pointer"
                                                >
                                                    <CustomImageHandler
                                                        photoPath={getCorrectImageThumbnail(
                                                            !!attachment?.externalLink,
                                                            attachment?.originalName ?? '',
                                                            attachment?.thumbPath ?? '',
                                                            attachment?.path
                                                        )}
                                                        altTag="Event"
                                                        thumbnailPath={attachment?.thumbnail}
                                                        placeholder={getCorrectImageThumbnail(
                                                            !!attachment?.externalLink,
                                                            attachment?.originalName ?? '',
                                                            attachment?.thumbPath ?? '',
                                                            attachment?.path
                                                        )}
                                                        style={{ width: 85, aspectRatio: '1/1' }}
                                                        classes="default-radius img-cover"
                                                    />
                                                </a>
                                            ) : (
                                                <FilePlaceholder
                                                    file={attachment}
                                                    hideText
                                                    classContent="default-radius"
                                                    classImage="img-cover"
                                                    styleContent={{ height: '85px', width: '85px' }}
                                                    nameFancybox="attachments"
                                                />
                                            )}
                                            <div className="ml-3 w-100">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <a
                                                        target="_blank"
                                                        className="text-dark font-weight-semi-bold word-break"
                                                        href={
                                                            getExternalLink(attachment?.externalLink) ??
                                                            attachment?.path ??
                                                            '/icons/filePlaceholder.png'
                                                        }
                                                        rel="noreferrer"
                                                    >
                                                        {t('general.file')} {index + 1}/{state?.attachments?.length} -{' '}
                                                        <span className="text-muted">{state?.id}</span>
                                                    </a>
                                                    {!attachment?.isApproved && showInfluencerFeedback && (
                                                        <BsFillTrashFill
                                                            className="text-muted cursor-pointer font-18"
                                                            onClick={() => {
                                                                setSelectedFile(attachment);
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                                <p className="text-muted mb-0">{formatDate(attachment?.createdAt)}</p>
                                                {!!attachment?.externalLink ? (
                                                    <a
                                                        href={getExternalLink(attachment?.externalLink)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {t('general.externalLink')}
                                                    </a>
                                                ) : (
                                                    <span
                                                        className="cursor-pointer text-primary"
                                                        onClick={() =>
                                                            downloadFile(
                                                                attachment?.externalLink ??
                                                                    attachment?.path ??
                                                                    '/icons/filePlaceholder.png',
                                                                attachment?.originalName
                                                            )
                                                        }
                                                    >
                                                        {t('general.download')}
                                                    </span>
                                                )}
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <p className="font-weight-semi-bold mb-0">
                                                        {t('general.button.approve')}
                                                    </p>
                                                    <ToggleSwitch
                                                        onChange={() =>
                                                            handleSwitch(attachment?.id, attachment?.isApproved)
                                                        }
                                                        small={true}
                                                        key={attachment?.id}
                                                        disabled={statusesToHideApproveButton?.includes(state?.status)}
                                                        id={`Attachment - ${attachment?.id}`}
                                                        checked={attachment?.isApproved}
                                                    />
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            );
                        })
                    ) : (
                        <p>{t('influencer.planner.no_files_uploaded')}</p>
                    )}
                </Row>
            </Fancybox>
            <Box className="d-flex flex-column" sx={{ maxHeight: '400px' }}>
                <Box className="mb-3">
                    <Tabs
                        defaultValue={currTab}
                        value={currTab}
                        onChange={handleChangeTab}
                        aria-label="basic tabs example"
                        TabIndicatorProps={{ style: { background: 'var(--primary)' } }}
                    >
                        <Tab
                            label="Influencer Feedback"
                            className={`${!showInfluencerFeedback && 'd-none'}`}
                            {...a11yProps(0)}
                        />
                        <Tab label="Management" className={`${!showClientComments && 'd-none'}`} {...a11yProps(1)} />
                    </Tabs>
                </Box>
                <div ref={lastMessageRef} className="overflow-auto pr-2 w-100 h-100">
                    <TabPanel value={currTab} index={0}>
                        {!!state?.comments?.length ? (
                            state?.comments?.map(comment => (
                                <MessageBody
                                    comment={comment as any}
                                    key={comment?.id}
                                    haveFiles={false}
                                    senderData={comment?.sentBy}
                                    isMyMessage={+comment?.sentBy?.id === +user?.id}
                                />
                            ))
                        ) : (
                            <p>{t('influencer.planner.noComments')}</p>
                        )}
                    </TabPanel>
                    <TabPanel value={currTab} index={1}>
                        {!!state?.clientComments?.length ? (
                            state?.clientComments?.map(comment => (
                                <MessageBody
                                    comment={comment as any}
                                    key={comment?.createdAt + comment.id}
                                    haveFiles={false}
                                    senderData={comment?.sentBy}
                                    isMyMessage={+comment?.sentBy?.id === +user?.id}
                                />
                            ))
                        ) : (
                            <p>{t('influencer.planner.noComments')}</p>
                        )}
                    </TabPanel>
                </div>
            </Box>
        </>
    );
};

export default CalendarManageRightCard;
